import { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { ImageLanding } from "components/organisms/ImageLanding";
import { getImage } from "lib/actions";
import { ImageDTO } from "types";
export async function getServerSideProps({
  params
}: {
  params: {
    imageId: string;
  };
}) {
  const imageId = params.imageId;
  const {
    image,
    error
  } = await getImage(imageId as string);
  return {
    props: {
      image: image ?? null,
      error: error ?? null
    }
  };
}
export default function ImagePage({
  image: initialImage,
  error
}: {
  image: ImageDTO | null;
  error: string | null;
}) {
  const router = useRouter();
  const [image, setImage] = useState<ImageDTO | null>(initialImage);
  useEffect(() => {
    const fetchImage = async (imageId: string) => {
      const {
        image,
        error
      } = await getImage(imageId);
      if (error) {
        setImage(null);
      } else {
        setImage(image || null);
      }
    };
    if (router.query.imageId) {
      fetchImage(router.query.imageId as string);
    } else {
      console.log("No imageId found in router.query");
    }
  }, [router.query.imageId]);
  if (!image || !!error) {
    return <>
                <Head>
                    <title>CharGen: Image Not Found</title>
                    <meta name="description" content="The requested image could not be found." />
                </Head>
                <div style={{
        height: "50vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
                    Image not found
                </div>
            </>;
  }
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  const {
    theme,
    username,
    description,
    npcFieldsMap,
    requestType
  } = image.imageMetadata;
  const race = npcFieldsMap?.["Race"] || "";
  const characterClass = npcFieldsMap?.["Class"] || "";
  const occupation = npcFieldsMap?.["Occupation"] || "";
  const monster = npcFieldsMap?.["Monster"] || "";
  const itemName = npcFieldsMap?.["Item Name"] || "";
  const landscapeName = npcFieldsMap?.["Landscape Name"] || "";
  const itemType = npcFieldsMap?.["Item Type"] || "";
  const battlemapName = npcFieldsMap?.["Battlemap Name"] || "";
  const npcDescription = [race, characterClass, occupation, monster].filter(Boolean).join(" ");
  const characterDescription = npcFieldsMap?.["description"] || "";
  const baseTitle = `CharGen: Fantasy AI Image Generator - ${formatRequestType(requestType)} Art by ${username}`;
  const baseDescription = `Explore AI-generated ${requestType?.toLowerCase() || ""} art`;
  const titleOptions = {
    NPC: `${baseTitle} - ${npcDescription}`,
    CHARACTER: `${baseTitle} - ${characterDescription} in ${theme}`,
    MONSTER: `${baseTitle} - ${npcDescription} in ${theme}`,
    ITEM: `${baseTitle} - ${itemName} - ${itemType}`,
    LANDSCAPE: `${baseTitle} - ${landscapeName}`,
    BATTLEMAP: `${baseTitle} - ${battlemapName}`,
    EDIT: `${baseTitle} - ${npcDescription}`,
    VIDEO: `${baseTitle} - ${npcDescription}`,
    AUDIO: `${baseTitle} - ${npcDescription}`
  };
  const descriptionOptions = {
    NPC: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D NPC art with CharGen.`,
    CHARACTER: `${baseDescription} created by ${username}. ${description} Generate your own D&D character art with CharGen.`,
    MONSTER: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D monster art with CharGen.`,
    ITEM: `${baseDescription} featuring ${itemName} - ${itemType} created by ${username}. Generate your own Fantasy item art with CharGen.`,
    LANDSCAPE: `${baseDescription} featuring ${landscapeName} created by ${username}. Generate your own Fantasy landscape art with CharGen.`,
    BATTLEMAP: `${baseDescription} featuring ${battlemapName} created by ${username}. Generate your own D&D battlemaps with CharGen.`,
    EDIT: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Art with CharGen.`,
    VIDEO: `${baseDescription} video featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Videos with CharGen.`,
    AUDIO: `${baseDescription} audio featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Audio with CharGen.`
  };
  const metaTitle = titleOptions[requestType];
  const metaDescription = descriptionOptions[requestType];
  const metaTags = {
    metaTitle,
    metaDescription
  };
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
                <title>{metaTags.metaTitle}</title>
                <meta name="description" content={metaTags.metaDescription} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:url" key="og:url" content={`https://char-gen.com/images/${image.id}`} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <link rel="canonical" href={`https://char-gen.com/images/${image.id}`} />
                <meta property="og:title" key="og:title" content={metaTags.metaTitle} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:description" key="og:description" content={metaTags.metaDescription} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image" key="og:image" content={image.image} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image:secure_url" key="og:image:secure_url" content={image.image} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta name="twitter:site" content="@char_genAI" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta name="twitter:title" content={metaTags.metaTitle} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta name="twitter:description" content={metaTags.metaDescription} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta name="twitter:image" content={image.image} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <link rel="preload" as="image" href={image.webpImage || image.image} />
                <script type="application/ld+json">
                    {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          url: `https://char-gen.com/images/${image.id}`,
          name: metaTitle,
          description: metaDescription,
          author: {
            "@type": "Person",
            name: username
          },
          contentUrl: image.webpImage || image.image
        })}
                </script>
            </Head>
            <ImageLanding image={image} data-sentry-element="ImageLanding" data-sentry-source-file="index.tsx" />
        </>;
}